if (!customElements.get('cart-set-upsell-panel')) {
  customElements.define(
    'cart-set-upsell-panel',
    class CartSetUpsellPanel extends HTMLElement {
      constructor() {
        super();

        this.cart = document.querySelector('cart-drawer');

        this.addEventListener(
          'keyup',
          (evt) => evt.code === 'Escape' && this.reject()
        );
        this.addEventListener('click', (evt) => {
          if (evt.target === this) this.reject();
        });

        document.addEventListener('cart-set-upsell:show', (evt) => {
          this.show(evt.detail.panel, evt.detail.addToCartResponse);
        });
      }

      connectedCallback() {
        this.inner = this.querySelector('.drawer__inner');
        this.inner?.addEventListener('click', this.onActionClick.bind(this));
        this.inner?.addEventListener(
          'mousedown',
          this.onImagesDragStart.bind(this)
        );
      }

      // The scrollbar for .cart-set-upsell__images is hidden by design, so
      // desktop mouse users lose their only affordance for scrolling it
      // unless we drive scrollLeft from mouse movement ourselves — touch/
      // trackpad swipe already scrolls natively via the element's own
      // overflow: auto and needs none of this.
      onImagesDragStart(evt) {
        const imagesEl = evt.target.closest('.cart-set-upsell__images');
        if (!imagesEl) return;

        evt.preventDefault();

        const onMove = this.onImagesDragMove.bind(this);
        this.dragState = {
          imagesEl,
          startX: evt.pageX,
          scrollLeft: imagesEl.scrollLeft,
          moved: false,
          onMove
        };
        imagesEl.classList.add('cart-set-upsell__images--dragging');

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', this.onImagesDragEnd.bind(this), {
          once: true
        });
      }

      onImagesDragMove(evt) {
        if (!this.dragState) return;

        const { imagesEl, startX, scrollLeft } = this.dragState;
        const delta = evt.pageX - startX;
        if (Math.abs(delta) > 3) this.dragState.moved = true;
        imagesEl.scrollLeft = scrollLeft - delta;
      }

      onImagesDragEnd() {
        if (!this.dragState) return;

        const { imagesEl, moved, onMove } = this.dragState;
        imagesEl.classList.remove('cart-set-upsell__images--dragging');
        document.removeEventListener('mousemove', onMove);

        // A real drag shouldn't also fire the PDP link click it started on.
        if (moved) {
          imagesEl.addEventListener('click', (e) => e.preventDefault(), {
            capture: true,
            once: true
          });
        }

        this.dragState = null;
      }

      show(panelFragment, addToCartResponse) {
        if (!this.inner || !panelFragment) return;

        this.addToCartResponse = addToCartResponse;
        this.inner.innerHTML = '';
        this.inner.appendChild(panelFragment);
        this.setActiveElement(document.activeElement);

        // Same "wrap in a timeout so the transition reliably triggers" quirk
        // as CartDrawer.open() relies on.
        setTimeout(() => {
          this.classList.add('animate', 'active');
        });

        this.addEventListener(
          'transitionend',
          () => {
            trapFocus(this, this.inner);
          },
          { once: true }
        );

        document.body.classList.add('overflow-hidden');

        window.S8Analytics?.trackUpsellInteraction({
          context: 'set_upsell',
          action: 'show'
        });
      }

      // Slides the panel out and clears its own focus trap — does not touch
      // the cart drawer. The drawer is opened separately (dismissAndShowCart)
      // since it never opens until the interstitial is dismissed.
      close() {
        this.classList.remove('active');
        removeTrapFocus(this.activeElement);
      }

      // Opens the cart drawer with the given add-to-cart response, then
      // slides the interstitial out — used by close/reject (original
      // single-item response) and by a completed upgrade (the swapped-cart
      // response). The drawer's own open() call sets body overflow-hidden,
      // so this hands scroll-lock ownership over to it as it takes over.
      dismissAndShowCart(response) {
        if (this.cart && response?.sections) {
          this.cart.renderContents(response);
        }
        this.close();
      }

      reject() {
        window.S8Analytics?.trackUpsellInteraction({
          context: 'set_upsell',
          action: 'reject'
        });
        this.dismissAndShowCart(this.addToCartResponse);
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      onActionClick(evt) {
        const actionEl = evt.target.closest('[data-cart-set-upsell-action]');
        if (!actionEl) return;

        const action = actionEl.dataset.cartSetUpsellAction;
        if (action === 'close' || action === 'reject') {
          this.reject();
        } else if (action === 'upgrade') {
          this.handleUpgrade(actionEl);
        }
      }

      handleUpgrade(button) {
        if (button.getAttribute('aria-disabled') === 'true') return;

        const contentEl = this.inner.querySelector(
          '[data-cart-set-upsell-content]'
        );
        const bundleVariantId = contentEl?.dataset.upgradeVariantId;
        // The line item's unique key (variant_id:properties_hash), not the
        // bare variant id — /cart/update.js's `updates` map is keyed by
        // line/key, not variant id, so a variant id here would silently
        // fail to match (or match the wrong line), leaving the original
        // item in the cart alongside the newly-added bundle.
        const originalLineKey = this.addToCartResponse?.key;

        if (!bundleVariantId || !originalLineKey) {
          console.error(
            '[cart-set-upsell] missing bundle variant id or original line key for upgrade',
            {
              bundleVariantId,
              originalLineKey
            }
          );
          return;
        }

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        button.querySelector('.loading__spinner')?.classList.remove('hidden');

        const sectionsToRender = this.cart?.getSectionsToRender
          ? this.cart.getSectionsToRender()
          : [];
        const sectionIds = sectionsToRender.map((section) => section.id);

        // Remove the just-added single item, then add the bundle — the
        // customer must never see the original item in a visible cart
        // before the swap completes, so the panel stays covering the
        // drawer (spinner showing) for this whole round-trip; only on
        // success, once the drawer's DOM reflects the final bundle-swapped
        // state, does the panel close/slide out.
        fetch(window.routes.cart_update_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ updates: { [originalLineKey]: 0 } })
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              throw new Error(response.description || 'Cart update failed');

            return fetch(window.routes.cart_add_url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
              },
              body: JSON.stringify({
                items: [
                  {
                    id: bundleVariantId,
                    quantity: 1,
                    properties: {
                      _upsell_context: 'set_upsell',
                      _upsell_list_id: 'cart_set_upsell_interstitial'
                    }
                  }
                ],
                sections: sectionIds,
                sections_url: window.location.pathname
              })
            });
          })
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              throw new Error(response.description || 'Cart add failed');

            // /cart/add.js was called with the bulk `items` array format, so
            // the added line lives at response.items[0] — not at the top
            // level of response (that shape only applies to the single-item,
            // non-array request form).
            const addedItem = response.items?.[0] || {};
            window.S8Analytics?.trackUpsellAddToCart({
              context: 'set_upsell',
              listId: 'cart_set_upsell_interstitial',
              listName: 'Cart set upsell interstitial',
              items: [
                {
                  item_id: addedItem.id ?? addedItem.variant_id,
                  item_name: addedItem.product_title,
                  price: addedItem.price,
                  quantity: addedItem.quantity
                }
              ]
            });

            this.dismissAndShowCart(response);
          })
          .catch((e) => {
            console.error('[cart-set-upsell] upgrade failed', e);
            button.removeAttribute('aria-disabled');
            button.classList.remove('loading');
            button.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }
    }
  );
}
